import React from 'react'
import { useAuth } from '../../AuthContext';

// redux
import { useAppSelector } from '../../redux/store';

// excel pacakge
import * as xlsx from 'xlsx';

// firebase
import { db, auth } from '../../firebaseConfig';
import { Timestamp } from 'firebase/firestore';

// functions
import {
    fetchCampaignCallLogsForReport, fetchCampaignLeadsForReport, fetchEmployeeCallLogsForReport, fetchEmployeesLeadsForReport,
    fetchEmployeeAdmissionStatusForReport, fetchLeadsBasedOnAdmissionStatus
} from '../../pages/Clients/Common/Functions';
import getCurrentScreen from '../../Helper/GetScreen';


interface ReportModalsProps {
    reportType: string;
    onClose: (message: string) => void;
}
interface SettingsBoxesLayoutProps {
    reportType: string;
    // Add other required props
}
const ReportModals: React.FC<ReportModalsProps> = ({ reportType, onClose }) => {

    // global state
    const { isAdmin, isManager, isStaff } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    // Loadin state
    const [isGenerating, setIsGenerating] = React.useState<boolean>(false);

    // display state
    const [selectedTitle, setSelectedTitle] = React.useState<string>('');
    const [selectedHeaderValue, setSelectedHeaderValue] = React.useState<string>('');
    const [selectedConidtionalParameter, setSelectedConidtionalParameter] = React.useState<string>('');

    // date range picker
    const [startDate, setStartDate] = React.useState(convertToFirebaseTimestamp(new Date(), true));
    const [endDate, setEndDate] = React.useState(convertToFirebaseTimestamp(new Date(), false));

    // data state
    const [data, setData] = React.useState<any[]>([]);
    const [tempDataStorage, setTempDataStorage] = React.useState<any[]>([]);

    // Get headers from the first data object
    const headers = data.length > 0 ? Object.keys(data[0]) : [];

    const RenderRegularReportBox = (
        title: string,
        discription: string,
        disabled: boolean
    ) => {
        return (
            <div className={`max-w-sm p-6 border rounded-lg shadow-sm ${disabled
                ? "bg-gray-100 dark:bg-gray-700"
                : "bg-white dark:bg-gray-800"
                } border-gray-200 dark:border-gray-700`}>
                <a href="#">
                    <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{title}</h5>
                </a>
                <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">{discription}</p>
                <a href="#"
                    style={{
                        background: !disabled
                            ? 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)'
                            : 'none',
                    }}
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium text-center rounded-lg ${!disabled
                        ? "text-white hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                        : "text-gray-400 bg-gray-200 cursor-not-allowed"
                        }`}
                    onClick={() => !disabled ? handleGenerateReport(title) : undefined}
                >
                    {isGenerating && selectedTitle === title ? (
                        <div className='cursor-not-allowed'>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75 " fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating...
                        </div>
                    ) : (
                        <>
                            Generate
                            <svg className="rtl:rotate-180 w-3.5 h-3.5 ms-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5h12m0 0L9 1m4 4L9 9" />
                            </svg>
                        </>
                    )}
                </a>
            </div>
        );
    };

    const ReportBoxesLayout: React.FC<SettingsBoxesLayoutProps> = ({ reportType }) => {
        switch (reportType) {
            case 'Campaign Analytics':
                return (
                    <div className="grid grid-cols-3 gap-4">
                        {RenderRegularReportBox(
                            'based on call connectivity',
                            'Analyze the effectiveness of call connections to assess the campaign reach and engagement.',
                            false
                        )}
                        {RenderRegularReportBox(
                            'based on conversation ratio',
                            'Measuring the success of converting leads into outcomes to evaluate campaign effectiveness.',
                            false
                        )}
                        {RenderRegularReportBox(
                            'based on staff performance',
                            'Assessing individual team members contributions to gauge overall campaign success.',
                            true
                        )}
                    </div>
                );
            case 'Employee Analytics':
                return (
                    <div className="grid grid-cols-3 gap-4">
                        {RenderRegularReportBox(
                            'based on call connectivity',
                            'Analyze the effectiveness of call connections to assess the employees reach and engagement.',
                            false
                        )}
                        {RenderRegularReportBox(
                            'based on conversation ratio',
                            'Measuring the success of converting leads into outcomes to evaluate employees effectiveness.',
                            false
                        )}
                    </div>
                );
            case 'Admission Analytics':
                return (
                    <div className="grid grid-cols-3 gap-4">
                        {RenderRegularReportBox(
                            'based on admission status',
                            'Analyze the effectiveness of admission status to assess the admission reach and engagement.',
                            false
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    // handle input change of date and turn it into a firebase timestamp
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        console.log(name, value);

        // if the name is startDate then convert the date to a firebase time stamp with time set to 00:00:00 else set the time to 23:59:59
        if (name === 'startDate') {
            setStartDate(convertToFirebaseTimestamp(value, true));
        } else {
            setEndDate(convertToFirebaseTimestamp(value, false));
        }
    };

    // Helper function to format date to YYYY-MM-DD
    const formatDateForInput = (date: { toISOString: () => string; }) => {
        return date.toISOString().split('T')[0];
    };

    // handle generate report
    const handleGenerateReport = async (title: string) => {
        setSelectedTitle(title);
        const user = auth.currentUser;
        console.log('user >>>', user);
        
        if (startDate && endDate) {
            setIsGenerating(true)
            try {
                let resultData = [];
                switch (reportType) {
                    case 'Campaign Analytics':
                        if (title === 'based on call connectivity') {
                            const callLogs = await fetchCampaignCallLogsForReport(db, user, {
                                startDate: Timestamp.fromDate(startDate),
                                endDate: Timestamp.fromDate(endDate),
                                isManager: isManager && currentScreen === 'Manager' ? true : false,
                                isStaff: isStaff && currentScreen === 'Staff' ? true : false
                            });
                            resultData = callLogs.data?.callLogs || [];
                        } else if (title === 'based on conversation ratio') {
                            const leads = await fetchCampaignLeadsForReport(db, user, {
                                startDate: Timestamp.fromDate(startDate),
                                endDate: Timestamp.fromDate(endDate),
                                isManager: isManager && currentScreen === 'Manager' ? true : false,
                                isStaff: isStaff && currentScreen === 'Staff' ? true : false
                            });
                            resultData = leads.data?.leads || [];
                        }
                        break;
                    case 'Employee Analytics':
                        if (title === 'based on call connectivity') {
                            const callLogs = await fetchEmployeeCallLogsForReport(db, user, {
                                startDate: Timestamp.fromDate(startDate),
                                endDate: Timestamp.fromDate(endDate),
                                isManager: isManager && currentScreen === 'Manager' ? true : false,
                                isStaff: isStaff && currentScreen === 'Staff' ? true : false
                            });
                            resultData = callLogs.data?.callLogs || [];
                        } else if (title === 'based on conversation ratio') {
                            const leads = await fetchEmployeesLeadsForReport(db, user, {
                                startDate: Timestamp.fromDate(startDate),
                                endDate: Timestamp.fromDate(endDate),
                                isManager: isManager && currentScreen === 'Manager' ? true : false,
                                isStaff: isStaff && currentScreen === 'Staff' ? true : false
                            });
                            resultData = leads.data?.leads || [];
                        }
                        break;
                    case 'Admission Analytics':
                        if (title === 'based on admission status') {
                            const admissionStatus = await fetchEmployeeAdmissionStatusForReport(db, user, {
                                startDate: Timestamp.fromDate(startDate),
                                endDate: Timestamp.fromDate(endDate),
                                isManager: isManager && currentScreen === 'Manager' ? true : false,
                                isStaff: isStaff && currentScreen === 'Staff' ? true : false
                            });
                            resultData = admissionStatus.data?.admissionStatus || [];
                        }
                        break;
                }
                // For staff/manager, set Employee column to current user's displayName
                // if ((isStaff || isManager) && user?.displayName) {
                //     resultData = resultData.map(row => ({ ...row, Employee: user.displayName }));
                // }
                console.log('Generated Report Data: >>>', resultData);
                
                setData(resultData);
            } catch (error: any) {
                console.error('Error generating report:', error);
                alert('Failed to generate report: ' + (error && error.message ? error.message : JSON.stringify(error)));
            }
            setIsGenerating(false)
        }
    }

    // handle data exploration
    const handleDataExploration = async (id: string, employeeName: string, conditonalParameter: string) => {
        if (selectedTitle === 'based on admission status') {
            const leads = await fetchLeadsBasedOnAdmissionStatus(db, auth.currentUser, {
                startDate: Timestamp.fromDate(startDate),
                endDate: Timestamp.fromDate(endDate),
                assignedTo: id,
                admissionStatusName: conditonalParameter
            });
            if (leads.data?.leads.length > 0) {
                setSelectedHeaderValue(employeeName);
                setSelectedConidtionalParameter(conditonalParameter);
                setTempDataStorage(data);
                setData(leads.data?.leads);
            }
        }
    }

    const handleExcelDownload = () => {
        const ws = xlsx.utils.json_to_sheet(data);
        const wb = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, selectedTitle.toLocaleUpperCase());
        xlsx.writeFile(wb, `${selectedTitle.toLocaleUpperCase()}_${new Date()}.xlsx`);
    }

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative ">
                <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 p-2 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/report-svgrepo-com-white.svg`} alt="Lead Icon" />
                        </div>
                        <h2 className="text-xl font-semibold text-black">{reportType}</h2>
                    </div>
                    <button
                        onClick={() => onClose('')}
                        className="absolute top-4 right-4 font-semibold text-3xl"
                        style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent' }}
                    >
                        <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
                    </button>
                </div>

                {data.length <= 0 ?
                    (
                        // Main Content
                        <>
                            {/* Date Range Picker */}
                            <div className="grid grid-cols-2 gap-6 mt-8">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700">Start</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={formatDateForInput(startDate)}
                                        onChange={handleInputChange}
                                        placeholder="Select Date"
                                        max={formatDateForInput(new Date())}
                                        required
                                        className="w-full px-4 py-2 border border-gray-200 rounded-md mt-2 focus:outline-none focus:border-blue-500"
                                        style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700">End</label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={formatDateForInput(endDate)}
                                        onChange={handleInputChange}
                                        placeholder="Select Date"
                                        min={formatDateForInput(startDate)}
                                        required
                                        className="w-full px-4 py-2 border border-gray-200 rounded-md mt-2 focus:outline-none focus:border-blue-500"
                                        style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                    />
                                </div>
                            </div>

                            {/* Report selection area */}
                            <div className="mt-8 mb-4 mx-2 max-h-[500px] overflow-y-scroll">
                                <ReportBoxesLayout reportType={reportType} />
                            </div>
                        </>
                    )
                    : (
                        // Table
                        <div className="mt-8">
                            <h2 className="text-xl font-semibold text-black">
                                {selectedTitle.toLocaleUpperCase()}
                                {selectedConidtionalParameter && ` - ${selectedConidtionalParameter}`}
                            </h2>
                            <div className="flex flex-col gap-2">
                                {selectedHeaderValue && <span>Assigned To : {selectedHeaderValue} </span>}
                                <span>{formatDateRange(startDate, endDate)}</span>
                            </div>
                            <div className="w-full max-h-[500px] mt-5 overflow-x-auto">
                                <table className="w-full min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {headers?.filter(header => !['id', 'ID', 'Id'].includes(header)).map((header, index) => (
                                                <th
                                                    key={index}
                                                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50 ${header === 'Employee' || header === 'Campaign' ? 'left-0 z-10' : ''}`}
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {data?.map((row, rowIndex) => (
                                            <tr
                                                key={rowIndex}
                                                className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                            >
                                                {headers?.filter(header => !['id', 'ID', 'Id'].includes(header)).map((header, colIndex) => (
                                                    <td
                                                        key={`${rowIndex}-${colIndex}`}
                                                        className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer ${header === 'Employee' || header === 'Campaign' ? 'sticky left-0 bg-inherit' : ''}`}
                                                        onClick={() => handleDataExploration(row?.Id, row?.Employee, header)}
                                                    >
                                                        {row[header]}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex justify-between items-center mt-8 px-2">
                                <a href="#"
                                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-center rounded-lg text-white bg-gray-600 hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-gray-300"
                                    onClick={() => tempDataStorage ? (setData(tempDataStorage), setTempDataStorage([])) : setData([])}
                                >
                                    <svg className="rtl:rotate-180 w-3.5 h-3.5 me-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5H1m0 0L5 1M1 5l4 4" />
                                    </svg>
                                    Go back
                                </a>
                                {currentScreen !== 'Staff' && (
                                    <a href="#"
                                        style={{
                                            background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)'
                                        }}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-center rounded-lg text-white hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                                        onClick={handleExcelDownload}
                                    >
                                        Download
                                        <svg className="rtl:rotate-180 w-3.5 h-3.5 ms-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 5h12m0 0L9 1m4 4L9 9" />
                                        </svg>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

            </div>
        </div>
    )
}

// Helper functions

// Convert date's time to start and end of the day
const convertToFirebaseTimestamp = (
    date: string | Date,
    isStartDate: boolean = true
): Date => {
    const dateObj = new Date(date);

    if (isStartDate) {
        dateObj.setHours(5, 30, 0, 0);
    } else {
        dateObj.setDate(dateObj.getDate() + 1);
        dateObj.setHours(5, 29, 59, 999);
    }

    return dateObj;
};

// Convert dates to formatted string range
const formatDateRange = (startDate: any, endDate: any) => {
    const formatDate = (date: { getDate: () => { (): any; new(): any; toString: { (): string; new(): any; }; }; getMonth: () => number; getFullYear: () => any; }) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export default ReportModals
